import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Avatar,
    Skeleton,
    Stack,
    IconButton,
    Modal,
    Tooltip,
} from "@mui/material";
import { ArrowForward, Mic, Search, Close as CloseIcon } from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { RiHospitalLine } from 'react-icons/ri';
import { useNavigate } from "react-router-dom";
import { get, post } from "../../utility/fetchWeb";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useDrawer } from "../../contexts/DrawerContext";
import MapSelector from "../Layout/MapSelector";
import VoiceRecorder from '../inputs/VoiceRecorder';
import Select from 'react-select';
import PromoBanner from "../Layout/PromoBanner";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LaboratoryChatModal from "../modals/LaboratoryChatModal";
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import VideoCalling from '../Layout/VideoCalling';
import Pagination from "../Layout/Pagination";

export default function OtherHealthCareProvidersPage() {
    const [selectedCategory, setSelectedCategory] = useState("General");
    const navigate = useNavigate();
    const [data, setData] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [engagedDoctors, setEngagedDoctors] = React.useState([]);
    const [searchText, setSearchText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [otherPractitioners, setOtherPractitioners] = useState([]);
    const [filteredPractitioners, setFilteredPractitioners] = useState([]);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [recommendedProviders, setRecommendedProviders] = useState([]);
    const [showVoiceResults, setShowVoiceResults] = useState(false);
    const [targetedAds, setTargetedAds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [labServicesModalOpen, setLabServicesModalOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [labServicesLoading, setLabServicesLoading] = useState(false);
    const [labServiceSearch, setLabServiceSearch] = useState('');
    const [isVoiceLabServiceModalOpen, setIsVoiceLabServiceModalOpen] = useState(false);
    const [labServices, setLabServices] = useState([]);
    const [filteredLabServices, setFilteredLabServices] = useState([]);

    const [isChatModalOpen, setIsChatModalOpen] = useState(false);
    const [selectedProviderForChat, setSelectedProviderForChat] = useState(null);

    const [showVideoChatModal, setShowVideoChatModal] = useState(false);
    const [startAudioOnly, setStartAudioOnly] = useState(false);
    const [appId, setAppId] = useState(process.env.REACT_APP_AGORA_KEY);
    const [channel, setChannel] = useState("call");
    const [token, setToken] = useState(null);
    const [uid, setUid] = useState(sessionStorage.getItem("userId") || "defaultUid");
    const [selectedProviderForCall, setSelectedProviderForCall] = useState(null);
    const [callId, setCallId] = useState(null); // Add callId state

    const name = sessionStorage.getItem("name").toString();
    const query = new URLSearchParams(window.location.search);
    const chatId = query.get("rrt");

    const handleLocationSelect = (location, address) => {
        console.log("Selected Location:", location);
        console.log("Selected Address:", address);
    };

    const { toggleAdmissionDetailsDrawer, setComponent } = useDrawer();

    const getEngagedDoctors = async () => {
        try {
            const response = await get(`/Doctors/EngagedDoctors/${sessionStorage.getItem("userId")}`);
            console.log('Response:', response);
            setEngagedDoctors(response);
        } catch (error) {
            console.error("Error fetching doctors:", error);
        }
    };

    const handleToggle = () => {
        setComponent("chat");
    };

    const getTargetedAds = async () => {
        try {
            const userId = sessionStorage.getItem('userId');
            const pageName = 'All Locations';
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/connecthealthadmin/api/ad/find-ad?Id=${userId}&page=${pageName}`);

            if (response.ok) {
                const data = await response.json();
                const transformedAds = data.map(ad => ({
                    id: ad.id,
                    imageUrl: ad.bannerImageUrl,
                    title: ad.title,
                    subtitle: ad.description,
                    redirectUrl: ad.redirectUrl,
                    status: 1,
                    isTargeted: true
                }));
                setTargetedAds(transformedAds);
            }
        } catch (error) {
            console.error('Error fetching targeted ads:', error);
            setTargetedAds([]);
        }
    };

    const getHealthCareProvider = async (page = 1) => {
        setIsLoading(true);
        try {
            let response = await get(`/HealthCareProvider/GetAllHealthCareProviderList`);
            console.log('Response:', response);
            const filteredList = response?.resultList
                .filter((item) => item?.healthCareProviderCategory?.id !== 1)
                .filter((item) => item?.healthCareProviderCategory?.id !== 2)
                .filter((item) => item?.healthCareProviderCategory?.id !== 4)
                .filter((item) => item?.healthCareProviderCategory?.id !== 5);

            setData(filteredList);
            setFilteredData(filteredList);
            setTotalPages(response?.totalPages || 1);
            setCurrentPage(page);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching providers:', error);
            setIsLoading(false);
        }
    };

    const getOtherPractitioners = async (page = 1) => {
        setIsLoading(true);
        try {
            const response = await get(`/Doctors/other-practitioners-list/true?pageNumber=${page}&pageSize=10`);
            if (response?.data.recordList) {
                setOtherPractitioners(response.data.recordList);
                setFilteredPractitioners(response.data.recordList);
                setTotalPages(response?.data.metadata?.totalPages || 1);
                setCurrentPage(response?.data.metadata?.page || page);
            }
        } catch (error) {
            console.error('Error fetching other practitioners:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const filterOtherPractitioners = async (query, page = 1) => {
        setIsLoading(true);
        try {
            const response = await get(`/Doctors/filter-other-list/true?pageNumber=${page}&pageSize=10&filterOn=PractitionerName&filterQuery=${query}`);
            if (response?.data.recordList) {
                setFilteredPractitioners(response.data.recordList);
                setTotalPages(response?.data.metadata?.totalPages || 1);
                setCurrentPage(response?.data.metadata?.page || page);
            }
        } catch (error) {
            console.error('Error filtering practitioners:', error);
            setFilteredPractitioners([]);
        } finally {
            setIsLoading(false);
        }
    };

    const getCategories = async () => {
        try {
            const response = await get("/Categories/SpecialistCategories");
            const newResponse = [
                { id: 0, name: "All", description: "All Categories" },
                ...response.data,
            ];
            setCategories(newResponse.map((cat) => ({ value: cat.name, label: cat.name })));
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    function getRoleTitle(roles) {
        if (!Array.isArray(roles) || roles.length === 0) return "No Role";

        let roleString = "";

        const hasRole = (roleName) => roles.includes(roleName);
        if (hasRole("Doctor")) {
            roleString = "Doctor";
        } else if (hasRole("Nurse") && hasRole("checkin")) {
            roleString = "Checkin Nurse";
        } else if (hasRole("Nurse") && hasRole("Local Admin")) {
            roleString = "Admin Nurse";
        } else if (hasRole("Pharmacist")) {
            roleString = "Pharmacist";
        } else if (roles.length === 1) {
            roleString = roles[0];
        } else if (hasRole("Health Finance Admin")) {
            roleString += " - Admin Manager";
        } else {
            roleString = roles[0];
        }

        if (hasRole("Super Admin") || roles.length > 7) {
            roleString += " - Super Admin";
        }

        return roleString;
    }

    const handleSearchChange = async (event, page = 1) => {
        const { value } = event.target;
        setSearchQuery(value);

        if (value.length >= 2) {
            try {
                setIsLoading(true);
               filterOtherPractitioners(value, page);
            } catch (error) {
                setFilteredPractitioners([]);
            } finally {
                setIsLoading(false);
            }
        } else {
            getOtherPractitioners();
        }
    };

    const handleMicClick = () => {
        setIsVoiceModalOpen(true);
    };

    const handleTranscript = async (transcript) => {
        setIsLoading(true);
        setSearchQuery(transcript);
        setIsListening(true);
        try {
            setShowVoiceResults(true);
            setIsVoiceModalOpen(false);
        } catch (error) {
            console.error('Error fetching providers:', error);
        } finally {
            setIsListening(false);
            setIsLoading(false);
        }
    };

    const handleCartClick = (e, provider) => {
        e.stopPropagation();
        setSelectedProvider(provider);
        setLabServicesModalOpen(true);
        fetchLabServices(provider.id);
    };

    const handleMessageClick = (e, provider) => {
        e.stopPropagation(); // Prevent the card's onClick from firing
        setSelectedProviderForChat(provider);
        setIsChatModalOpen(true);
    };

    const handleCloseLabServicesModal = () => {
        setLabServicesModalOpen(false);
        setSelectedProvider(null);
        setLabServices([]);
        setFilteredLabServices([]);
        setLabServiceSearch('');
    };

    const fetchLabServices = async (providerId) => {
        setLabServicesLoading(true);
        try {
            const response = await get(`/internallabservice/list/${providerId}/1/50/by-health-care-provider`);
            console.log('Lab Services Response:', response);

            const services = response?.data.recordList || response?.data || [];
            setLabServices(services);
            setFilteredLabServices(services);
        } catch (error) {
            console.error('Error fetching lab services:', error);
            setLabServices([]);
            setFilteredLabServices([]);
        } finally {
            setLabServicesLoading(false);
        }
    };

    const handleLabServiceSearchChange = (e) => {
        const searchValue = e.target.value;
        setLabServiceSearch(searchValue);

        if (searchValue.trim() === '') {
            fetchLabServices(selectedProvider.id);
        } else if (searchValue.length >= 2) {
            fetchFilteredLabServices(selectedProvider.id, searchValue);
        }
    };

    const fetchFilteredLabServices = async (providerId, searchQuery) => {
        setLabServicesLoading(true);
        try {
            const response = await get(`/internallabservice/filter-list/${providerId}/testName/${searchQuery}/1/50/by-health-care-provider`);
            console.log('Filtered Lab Services Response:', response);

            const services = response?.data.recordList || response?.data || [];
            setLabServices(services);
            setFilteredLabServices(services);
        } catch (error) {
            console.error('Error fetching filtered lab services:', error);
            const filtered = labServices.filter(service =>
                service.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                service.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                service.testName?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredLabServices(filtered);
        } finally {
            setLabServicesLoading(false);
        }
    };

    const handleLabServiceVoiceSearch = () => {
        setIsVoiceLabServiceModalOpen(true);
    };

    const handleLabServiceVoiceTranscript = async (transcript) => {
        setLabServiceSearch(transcript);

        if (transcript.trim().length >= 2) {
            await fetchFilteredLabServices(selectedProvider.id, transcript);
        }

        setIsVoiceLabServiceModalOpen(false);
    };

    const notifyCaller = async (chann, id) => {
        try {
            const res = await post(`/Posts/notify-call`, {
                receiverId: id,
                channelName: chann,
                callerId: Number(sessionStorage.getItem("userId")),
                callerName: sessionStorage.getItem("name"),
                userType: 2 // for practitioner 1 for patient
            });
            console.log(res)
            if (res?.data) {
                console.log("Caller notified successfully");
            } else {
                console.error("Failed to notify caller:", res);
            }
        } catch (error) {
            console.error("Error notifying caller:", error);
        }
    }

    const getAgoraDetails = async (receiverId) => {
        try {
            const res = await get(`/posts/generate-new-token?channelName=${`connecthealthpro_videocall-${uid}&${receiverId}`}&uid=${uid}`);
            if (res && res.token) {
                setToken(res.token);
                setUid(Number(res.uid) || uid);
                setAppId(res.appId || process.env.REACT_APP_AGORA_KEY);
                setChannel(res.channelName || "call");
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error fetching Agora token:", error);
            return false;
        }
    };

    const handleVideoCall = async (e, provider) => {
        e.stopPropagation();
        setSelectedProviderForCall(provider);
        const success = await getAgoraDetails(provider.id);
        if (success) {
            setStartAudioOnly(false);
            setCallId(Date.now()); // Generate unique callId for each call
            setShowVideoChatModal(true);
            notifyCaller(`${`connecthealthpro_videocall-${uid}&${provider.id}`}`, provider.id);
        }
    };

    const handleAudioCall = async (e, provider) => {
        e.stopPropagation();
        setSelectedProviderForCall(provider);
        const success = await getAgoraDetails(provider.id);
        if (success) {
            setStartAudioOnly(true);
            setCallId(Date.now()); // Generate unique callId for each call
            setShowVideoChatModal(true);
            notifyCaller(`${`connecthealthpro_audiocall-${uid}&${provider.id}`}`, provider.id);
        }
    };

    const handleCloseVideoChat = () => {
        setShowVideoChatModal(false);
        setToken(null);
        setSelectedProviderForCall(null);
        setCallId(null); // Reset callId when closing
    };

    useEffect(() => {
        getTargetedAds();
        getHealthCareProvider();
        getCategories();
        getEngagedDoctors();
        getOtherPractitioners();

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setCurrentLocation({ lat: latitude, lng: longitude });
                },
                (error) => {
                    console.error("Error getting current location:", error);
                }
            );
        }
    }, []);

    useEffect(() => {
        if (searchQuery.length >= 3) {
            filterOtherPractitioners(searchQuery);
        } else if (searchQuery.length === 0) {
            getOtherPractitioners();
        }
    }, [searchQuery]);

    return (
        <div className="w-100">
            <div className="search-section">
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="Search for health practitioners..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                    {(searchQuery) && (
                        <button
                            className="clear-button"
                            onClick={() => {
                                setSearchQuery('');
                            }}
                        >
                            ×
                        </button>
                    )}
                </div>
                <Tooltip title="Voice search for health practitioners" arrow placement="top">
                    <div className="filter-btn" onClick={handleMicClick}>
                        <Mic color='white' />
                    </div>
                </Tooltip>
            </div>

            <Box display="flex" flexDirection={"column"} justifyContent="flex-end" gap={2} mt={2} mb={3}>
                <Box sx={{ minWidth: 200 }}>
                    <Select
                        options={categories}
                        value={selectedCategory}
                        onChange={async (selectedOption) => {
                            setSelectedCategory(selectedOption);

                            if (selectedOption?.value === "All" || !selectedOption) {
                                getOtherPractitioners();
                            } else {
                                try {
                                    setIsLoading(true);
                                    const response = await get(`/Doctors/other-practitioners/profession/${selectedOption.value}`);
                                    if (response?.length > 0) {
                                        setFilteredPractitioners(response);
                                    } else {
                                        setFilteredPractitioners([]);
                                    }
                                } catch (error) {
                                    console.error('Error filtering by category:', error);
                                    setFilteredPractitioners([]);
                                } finally {
                                    setIsLoading(false);
                                }
                            }
                        }}
                        placeholder="Select Category"
                        isClearable
                        styles={{
                            menu: (provided) => ({
                                ...provided,
                                zIndex: 1300,
                            }),
                        }}
                    />
                </Box>

                <Box sx={{ flex: 1 }}>
                    <MapSelector
                        onLocationSelect={handleLocationSelect}
                        currentLocation={currentLocation}
                    />
                </Box>
                {targetedAds.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <PromoBanner ads={targetedAds} />
                    </Box>
                )}
            </Box>

            <>
                {isVoiceModalOpen && (
                    <VoiceRecorder
                        onTranscript={async (transcript) => {
                            if (transcript) {
                                await handleTranscript(transcript);
                                setShowVoiceResults(true);
                            }
                            setIsVoiceModalOpen(false);
                            setIsListening(false);
                        }}
                        provider="Other Health Care Providers"
                    />
                )}

                {isLoading ? (
                    <div className="pharmacy-list">
                        {[...Array(6)]?.map((_, index) => (
                            <div key={index} className="pharmacy-card">
                                <Skeleton variant="circular" width={56} height={56} />
                                <div className="pharmacy-info">
                                    <Skeleton variant="text" width="60%" height={20} />
                                    <Skeleton variant="text" width="80%" height={24} />
                                    <Skeleton variant="text" width="90%" height={16} />
                                </div>
                                <div className="doctor-info3">
                                    <Skeleton variant="text" width="70%" height={20} />
                                    <Skeleton variant="text" width="50%" height={16} />
                                </div>
                                <Skeleton variant="circular" width={24} height={24} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="pharmacy-list">
                        {filteredPractitioners?.length === 0 ? (
                            <Box sx={{ textAlign: "center", mt: 4 }}>
                                <Typography variant="h6" color="text.secondary">
                                    We couldn't find any health practitioner matching your search.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    You can browse through the available health practitioners, search by name, or try a different location.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {filteredPractitioners?.map((practitioner) => (
                                    <div key={practitioner?.id} className="pharmacy-card" onClick={() => navigate(`/profile?cat=caregiver&id=${practitioner?.id}`, { state: { providerData: practitioner } })}>
                                        <div className="pharmacy-icon">
                                            {practitioner?.profilePicture ? (
                                                <img
                                                    src={practitioner?.profilePicture.startsWith('http')
                                                        ? practitioner?.profilePicture
                                                        : `${process.env.REACT_APP_MEDIA_URL}${practitioner?.profilePicture}`
                                                    }
                                                    alt="Provider Logo"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }}
                                                    onError={(e) => {
                                                        const target = e.target;
                                                        if (target) {
                                                            target.style.display = 'none';
                                                            const parent = target.parentElement;
                                                            if (parent) {
                                                                const icon = document.createElement('div');
                                                                icon.innerHTML = '<RiHospitalLine style={{ fontSize: "24px", color: "white" }} />';
                                                                parent.appendChild(icon);
                                                            }
                                                        }
                                                    }}
                                                />
                                            ) : <RiHospitalLine style={{ fontSize: '24px', color: 'white' }} />}
                                        </div>

                                        <div className="pharmacy-info">
                                            <div className="pharmacy-type">{getRoleTitle(practitioner?.userRoles.map(role => role.role.name))}</div>
                                            <h4 className="pharmacy-name">
                                                {`${practitioner?.firstName} ${practitioner?.lastName}`}
                                            </h4>
                                            <p className="pharmacy-address">
                                                {practitioner?.healthCareProvider?.location || 'No location provided'}
                                            </p>
                                        </div>

                                        <div className="doctor-info3">
                                            <h5 className="doctor-name">
                                                {practitioner?.healthCareProvider?.name}
                                            </h5>
                                            <p className="doctor-role">
                                                {practitioner?.reviews} reviews
                                            </p>
                                        </div>

                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="Video Call" arrow>
                                                <IconButton size="medium" color="primary" onClick={(e) => handleVideoCall(e, practitioner)}>
                                                    <VideocamOutlinedIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Audio Call" arrow>
                                                <IconButton size="medium" color="primary" onClick={(e) => handleAudioCall(e, practitioner)}>
                                                    <PhoneOutlinedIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Message Provider" arrow>
                                                <IconButton size="medium" color="primary" onClick={(e) => handleMessageClick(e, practitioner)}>
                                                    <ChatBubbleOutlineIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                            {/* <Tooltip title="Lab Services" arrow>
                                        <IconButton size="small" color="primary" onClick={(e) => handleCartClick(e, practitioner)}>
                                            <ShoppingCartOutlinedIcon fontSize="medium" />
                                        </IconButton>
                                    </Tooltip> */}
                                        </Stack>
                                    </div>
                                ))}

                            </>
                        )}
                    </div>
                )}
            </>

            {!isLoading && filteredPractitioners?.length > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={getOtherPractitioners}
                />
            )}

            <Modal
                open={labServicesModalOpen}
                onClose={handleCloseLabServicesModal}
                aria-labelledby="lab-services-modal-title"
                aria-describedby="lab-services-modal-description"
                sx={{
                    '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.5)' }
                }}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '50vw',
                    maxWidth: '1200px',
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: 24,
                    p: 4,
                    height: '80vh',
                    overflowY: 'auto',
                    '::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none'
                }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography id="lab-services-modal-title" variant="h6" component="h2">
                            {selectedProvider?.name} - Available Lab Services
                        </Typography>
                        <IconButton onClick={handleCloseLabServicesModal}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>

                    <Box display="flex" gap={1} mb={2}>
                        <input
                            type="text"
                            placeholder="Search for lab services..."
                            value={labServiceSearch}
                            onChange={handleLabServiceSearchChange}
                            style={{
                                flex: 1,
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid #e0e0e0',
                                fontSize: '15px'
                            }}
                        />
                        <IconButton onClick={handleLabServiceVoiceSearch} color="primary">
                            <Mic />
                        </IconButton>
                    </Box>

                    {labServicesLoading ? (
                        <Stack gap={2}>
                            {[...Array(5)].map((_, idx) => (
                                <Skeleton key={idx} variant="rectangular" height={48} />
                            ))}
                        </Stack>
                    ) : (
                        <Stack gap={2}>
                            {filteredLabServices.length === 0 ? (
                                <Typography color="text.secondary">
                                    No lab services found.
                                </Typography>
                            ) : (
                                filteredLabServices.map((service) => (
                                    <Box key={service.id} sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        bgcolor: '#f8f9fa',
                                        boxShadow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.5,
                                        cursor: 'pointer',
                                        '&:hover': { backgroundColor: '#e9ecef' }
                                    }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                            {service.testName || service.name || service.serviceName}
                                        </Typography>
                                        {service.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                {service.description}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                            ₦{service.price || service.cost || service.amount || 'Price not available'}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                        </Stack>
                    )}
                </Box>
            </Modal>

            <LaboratoryChatModal
                open={isChatModalOpen}
                handleClose={() => {
                    setIsChatModalOpen(false);
                    setSelectedProviderForChat(null);
                }}
                selectedLab={selectedProviderForChat}
            />

            {showVideoChatModal && selectedProviderForCall && token && (
                <VideoCalling
                    appId={appId}
                    channel={channel}
                    token={token}
                    uid={uid}
                    callId={callId} // Pass callId prop
                    open={showVideoChatModal}
                    onClose={handleCloseVideoChat}
                    startWithVideoOff={startAudioOnly}
                    isAudio={startAudioOnly}
                />
            )}

            <style jsx>{`
                .pharmacy-page {
                    padding: 24px;
                    margin: 0 auto;
                }

                .search-section {
                    display: flex;
                    gap: 12px;
                    margin: 24px 0;
                    align-items: center;
                }

                .search-bar {
                    flex: 1;
                    position: relative;
                    display: flex;
                    align-items: center;
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 0 16px;
                    border: 1px solid #e9ecef;
                }

                .search-icon {
                    color: #6c757d;
                    margin-right: 12px;
                }

                .search-bar input {
                    flex: 1;
                    border: none;
                    background: transparent;
                    padding: 16px 0;
                    font-size: 16px;
                    outline: none;
                }

                .search-bar input::placeholder {
                    color: #6c757d;
                }

                .filter-btn:hover {
                    transform: scale(1.05);
                }

                .pharmacy-list {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .pharmacy-card {
                    display: flex;
                    align-items: center;
                    padding: 20px;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    cursor: pointer;
                    transition: transform 0.2s, box-shadow 0.2s;
                    gap: 16px;
                }

                .pharmacy-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
                }

                .pharmacy-icon {
                    width: 56px;
                    height: 56px;
                    background: linear-gradient(135deg, #6f42c1, #e83e8c);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 24px;
                    flex-shrink: 0;
                }

                .pharmacy-info {
                    flex: 1;
                    min-width: 0;
                }

                .pharmacy-type {
                    color: #6c757d;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }

                .pharmacy-name {
                    font-size: 18px;
                    font-weight: 600;
                    color: #212529;
                    margin: 0 0 8px 0;
                }

                .pharmacy-address {
                    color: #6c757d;
                    font-size: 14px;
                    margin: 0;
                }

                .doctor-info3 {
                    flex: 1;
                    min-width: 0;
                    text-align: right;
                }

                .doctor-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #212529;
                    margin: 0 0 4px 0;
                }

                .doctor-role {
                    color: #6c757d;
                    font-size: 12px;
                    margin: 0;
                }

                .arrow {
                    color: #6c757d;
                    flex-shrink: 0;
                }
            `}</style>
        </div>
    );
}
